import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CalendarOff, Trash2, Clock } from "lucide-react";

const KINDS = [
  { id: "vacation", label: "Vacation", color: "#0ea5e9" },
  { id: "sick",     label: "Sick day", color: "#dc2626" },
  { id: "flex",     label: "Flex day", color: "#d4af37" },
  { id: "unpaid",   label: "Unpaid leave", color: "#71717a" },
];
const KIND_META = Object.fromEntries(KINDS.map((k) => [k.id, k]));
const STATUS_META = {
  pending:  { label: "Pending",  bg: "#fef9c3", fg: "#854d0e" },
  approved: { label: "Approved", bg: "#dcfce7", fg: "#166534" },
  declined: { label: "Declined", bg: "#fee2e2", fg: "#991b1b" },
};

function isoToday() { return new Date().toISOString().slice(0, 10); }

export default function SonographerTimeOff() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [kind, setKind] = useState("vacation");
  const [start, setStart] = useState(isoToday());
  const [end, setEnd] = useState(isoToday());
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/time-off/mine");
      setRows(data || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Unable to load requests"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/time-off", { kind, start_date: start, end_date: end, note });
      setNote(""); await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    await api.delete(`/time-off/${id}`);
    await load();
  };

  return (
    <div className="card-elev mt-6" data-testid="time-off-widget">
      <div className="flex items-center gap-2 mb-4">
        <CalendarOff className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
        <h3 className="font-bold text-lg">Time-off requests</h3>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-4 gap-3 items-end" data-testid="time-off-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500">Type</label>
          <select
            value={kind} onChange={(e) => setKind(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3 bg-white"
            data-testid="time-off-kind"
          >
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500">From</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                 className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3" data-testid="time-off-start" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500">To</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                 className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3" data-testid="time-off-end" />
        </div>
        <button disabled={busy} className="btn-gold h-10 text-sm" data-testid="time-off-submit">
          {busy ? "Submitting\u2026" : "Request"}
        </button>
        <div className="sm:col-span-4">
          <input type="text" placeholder="Optional note for your manager…" value={note} maxLength={280}
                 onChange={(e) => setNote(e.target.value)}
                 className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm" data-testid="time-off-note" />
        </div>
        {err && <div className="sm:col-span-4 text-sm text-red-600" data-testid="time-off-error">{err}</div>}
      </form>

      <div className="mt-6">
        {rows.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="time-off-empty">No time-off requests yet.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {rows.map((r) => {
              const k = KIND_META[r.kind]; const s = STATUS_META[r.status];
              return (
                <li key={r.request_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`time-off-row-${r.request_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full" style={{ background: `${k.color}22`, color: k.color }}>{k.label}</span>
                      <span className="text-sm font-semibold">{r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}</span>
                    </div>
                    {r.note && <div className="text-xs text-zinc-500 mt-1">{r.note}</div>}
                    {r.decision_note && r.status !== "pending" && <div className="text-[11px] text-zinc-400 mt-1"><Clock className="inline w-3 h-3 mr-0.5" />Manager: {r.decision_note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full" style={{ background: s.bg, color: s.fg }} data-testid={`time-off-status-${r.request_id}`}>{s.label}</span>
                    {r.status === "pending" && (
                      <button onClick={() => cancel(r.request_id)} className="btn-ghost text-red-600 text-xs h-8 px-2" data-testid={`time-off-cancel-${r.request_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
